/**
 * UniFi Site Manager helpers.
 *
 * `unifi_site_map.attributes` stores the raw Site Manager `/v1/sites` row.
 * The per-site device and client totals live under `attributes.counts`;
 * nothing here invents a number — a missing block yields `null`.
 */
import type { Json } from '@pazportal/supabase';

export interface UnifiCounts {
  totalDevice: number | null;
  offlineDevice: number | null;
  wifiDevice: number | null;
  wiredDevice: number | null;
  gatewayDevice: number | null;
  offlineGatewayDevice: number | null;
  wifiClient: number | null;
  wiredClient: number | null;
  guestClient: number | null;
  wifiConfiguration: number | null;
  wanConfiguration: number | null;
  pendingUpdateDevice: number | null;
  criticalNotification: number | null;
}

const COUNT_KEYS: Array<keyof UnifiCounts> = [
  'totalDevice',
  'offlineDevice',
  'wifiDevice',
  'wiredDevice',
  'gatewayDevice',
  'offlineGatewayDevice',
  'wifiClient',
  'wiredClient',
  'guestClient',
  'wifiConfiguration',
  'wanConfiguration',
  'pendingUpdateDevice',
  'criticalNotification',
];

function asCount(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value);
  return null;
}

/** Parse `attributes.counts`. Returns null when the site has no counts block. */
export function parseUnifiCounts(attributes: Json | null | undefined): UnifiCounts | null {
  if (!attributes || typeof attributes !== 'object' || Array.isArray(attributes)) return null;
  const counts = (attributes as Record<string, unknown>).counts;
  if (!counts || typeof counts !== 'object' || Array.isArray(counts)) return null;
  const source = counts as Record<string, unknown>;
  const parsed = {} as UnifiCounts;
  let any = false;
  for (const key of COUNT_KEYS) {
    const v = asCount(source[key]);
    parsed[key] = v;
    if (v !== null) any = true;
  }
  return any ? parsed : null;
}

/** Devices currently online for a site, or null when the counts are unknown. */
export function unifiOnlineDevices(counts: UnifiCounts | null): number | null {
  if (!counts || counts.totalDevice === null) return null;
  return Math.max(counts.totalDevice - (counts.offlineDevice ?? 0), 0);
}

export type UnifiFreshness = 'live' | 'stale' | 'offline';

export const UNIFI_LIVE_MS = 10 * 60 * 1000;
export const UNIFI_STALE_MS = 30 * 60 * 1000;

/** Same thresholds as integration_status_snapshot(): live < 10 min, stale < 30 min. */
export function unifiFreshness(updatedAt: string | null | undefined, now: number = Date.now()): UnifiFreshness {
  if (!updatedAt) return 'offline';
  const age = now - new Date(updatedAt).getTime();
  if (!Number.isFinite(age)) return 'offline';
  if (age < UNIFI_LIVE_MS) return 'live';
  if (age < UNIFI_STALE_MS) return 'stale';
  return 'offline';
}
